import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { UseNavigateResult } from '@tanstack/react-router';

import { useMe } from '@/api/me';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import type { Estate } from '@/features/estates-property/api/estate';
import { getProjectEstatesOptions } from '@/features/projects/api/project-estates';

import type { useModal } from './new/use-modal';
import { Modal } from './new/modal';
import { useEstateQuery } from '../../features/estates-property/api/estate';

export interface CopyEstateModalProps {
  estateId: Estate['id'];
  navigate: UseNavigateResult<string>;
  modal: ReturnType<typeof useModal>;
}

const getCopyEstateDefaultValues = (
  estate: Estate | undefined,
  creatorId: string | undefined
): EstateSchemaType => {
  return {
    estateData: {
      name: estate?.name || '',
      propertyType: estate?.estateDetails?.propertyType || '',
      marketType: estate?.estateDetails?.marketType || '',
      tenantId: estate?.tenantId || '',
      projectId: estate?.projectId || '',
      creatorId,
      propertyLevel: PROPERTY_LEVELS.OBJECT,
    },
    addressData: {
      addressAutoComplete: estate?.address?.address || '',
      address: estate?.address?.address || '',
      postalCode: estate?.address?.postalCode || '',
      city: estate?.address?.city || '',
      street: estate?.address?.street || '',
      houseNumber: estate?.address?.houseNumber || '',
      countryId: estate?.address?.country?.id || '',
    },
  };
};

export const CopyEstateModal: React.FC<CopyEstateModalProps> = ({ estateId, navigate, modal }) => {
  const { t } = useTranslation();
  const schema = getCreateEstateSchema();
  const { data: me } = useMe();
  const { data: estate } = useEstateQuery({
    id: estateId,
    queryConfig: { enabled: modal.isOpen },
  });
  const queryClient = useQueryClient();

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: ([createdEstate]) => {
        queryClient.invalidateQueries({ queryKey: ['estates'] });
        if (createdEstate.projectId) {
          queryClient.invalidateQueries({
            queryKey: getProjectEstatesOptions({ projectId: createdEstate.projectId, query: {} })
              .queryKey,
          });
        }
        navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        toast.success(t('create-estate-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: getCopyEstateDefaultValues(estate, me?.id),
  });

  useEffect(() => {
    if (!estate) return;

    methods.reset(getCopyEstateDefaultValues(estate, me?.id));
  }, [estate, me?.id, methods]);

  const handleSubmit = async (args: EstateSchemaType) => {
    await createEstateAsync(args);
  };

  return (
    <Modal {...modal} title={t('add-new-estate')} className="max-w-max">
      <Modal.Body>
        <FormProvider {...methods}>
          <EstateForm
            closeModal={modal.dismiss}
            label={t('add-new-estate')}
            onSubmit={handleSubmit}
            options={{
              propertyLevelDisabled: true,
              tenantDisabled: true,
            }}
          />
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
};
